import { Drawer as MuiDrawer } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, IconButton } from '@vestigas/ui';

import { useUniversalFiltersContext } from '../../UniversalFiltersProvider';
import { SavedFilterNameDialog } from '../SavedFilterNameDialog';

import { FiltersDrawerBody } from './FiltersDrawerBody';
import { FiltersDrawerFooter } from './FiltersDrawerFooter';
import { useFiltersDrawerSaveActions } from './useFiltersDrawerSaveActions';

/**
 * Shell for advanced universal filter fields.
 *
 * Drawer visibility is provider-owned. The component renders nothing when the
 * page config has no drawer fields and remains usable without saved filters.
 */
export function FiltersDrawer() {
  const { closeDrawer, config, isDrawerOpen, store } =
    useUniversalFiltersContext();
  const { t } = useTranslation('universalFilters');
  const saveActions = useFiltersDrawerSaveActions();

  const drawerFields = useMemo(
    () => config.drawerFields ?? [],
    [config.drawerFields],
  );
  const hasDrawerFields = Boolean(drawerFields.length);

  if (!hasDrawerFields) {
    return null;
  }

  function handleReset() {
    store.trigger.resetSelectedDraft();
  }

  return (
    <>
      <MuiDrawer
        anchor="right"
        open={isDrawerOpen}
        slotProps={{
          paper: {
            className:
              'flex h-dvh min-h-0 w-md max-w-[100vw] flex-col bg-white/90 p-0 shadow-lg backdrop-blur-md',
          },
        }}
        onClose={closeDrawer}
      >
        <div className="flex min-h-0 flex-1 flex-col px-5 pt-5">
          <header className="flex shrink-0 items-center justify-between gap-3 pb-4">
            <h2 className="text-tiga-text text-lg font-semibold">
              {t(($) => $.drawer.heading)}
            </h2>
            <IconButton
              aria-label={t(($) => $.drawer.closeHeader.ariaLabel)}
              type="button"
              onClick={closeDrawer}
            >
              <Icon metaphor="clear" />
            </IconButton>
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto py-2">
            <FiltersDrawerBody />
          </div>
        </div>

        <FiltersDrawerFooter
          canSave={saveActions.canSave}
          hasSavedFiltersEnabled={saveActions.hasSavedFiltersEnabled}
          isDirty={saveActions.isDirty}
          isSavePending={saveActions.isSavePending}
          resetText={t(($) => $.drawer.footer.reset.text)}
          saveText={t(($) => $.drawer.footer.save.text)}
          searchText={t(($) => $.drawer.footer.search.text)}
          onReset={handleReset}
          onSave={saveActions.handleSave}
          onSearch={closeDrawer}
        />
      </MuiDrawer>
      {saveActions.hasSavedFiltersEnabled ? (
        <SavedFilterNameDialog
          isOpen={saveActions.isCreateDialogOpen}
          isPending={saveActions.isSavePending}
          submitText={t(($) => $.drawer.saveDialog.submit.text)}
          onClose={saveActions.closeCreateDialog}
          onSubmit={saveActions.handleCreateSubmit}
        />
      ) : null}
    </>
  );
}
